import { ApiResponse } from '../../types/api';
import type {
  NLTConfig,
  NLTPaginated,
  NLTPatchConfig,
  NLTPatchRun,
  NLTPatchVersion,
  SaveNLTConfigRequest,
  SavePatchConfigRequest,
  SavePatchConfigResponse,
  GeneratePatchRequest,
  NLTChatRequest,
  NLTChatResponse,
  NLTChatType,
  PatchDecisionRequest,
  PatchDecisionAcceptResponse,
  PatchDecisionRejectResponse,
} from '../../types/nlt';
import type { NltWorkflowPatchRequest, NltWorkflowPatchResponse } from '../../types/nltWorkflow';
import { api } from './client';

// ─── NLT (Natural Language Tuner) endpoints ───────────────────────────────────

export const nlt = {
  // ── Admin: NLT Configs ────────────────────────────────────────────────────

  listNLTConfigs: async (params?: {
    limit?: number;
    offset?: number;
    q?: string;
  }): Promise<ApiResponse<NLTPaginated<NLTConfig>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTConfig>>>('/admin/nlt/configs', { params });
    return response.data;
  },

  getLatestNLTConfig: async (): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.get<ApiResponse<NLTConfig>>('/admin/nlt/configs/latest');
    return response.data;
  },

  getNLTConfig: async (configId: string): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.get<ApiResponse<NLTConfig>>(`/admin/nlt/configs/${configId}`);
    return response.data;
  },

  saveNLTConfig: async (body: SaveNLTConfigRequest): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.post<ApiResponse<NLTConfig>>('/admin/nlt/configs/save', body);
    return response.data;
  },

  deleteNLTConfig: async (configId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(`/admin/nlt/configs/${configId}`);
    return response.data;
  },

  // ── User: Patch Configs ───────────────────────────────────────────────────

  savePatchConfig: async (
    integrationId: string,
    body: SavePatchConfigRequest
  ): Promise<ApiResponse<SavePatchConfigResponse>> => {
    const response = await api.post<ApiResponse<SavePatchConfigResponse>>('/nlt/configs', body, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  listPatchConfigs: async (
    integrationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchConfig>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchConfig>>>('/nlt/configs', {
      params: { integration_id: integrationId, ...params },
    });
    return response.data;
  },

  getPatchConfig: async (configId: string, integrationId: string): Promise<ApiResponse<NLTPatchConfig>> => {
    const response = await api.get<ApiResponse<NLTPatchConfig>>(`/nlt/configs/${configId}`, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  listConfigVersions: async (
    configId: string,
    integrationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchVersion>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchVersion>>>(`/nlt/configs/${configId}/versions`, {
      params: { integration_id: integrationId, ...params },
    });
    return response.data;
  },

  // ── User: Generate Patch ──────────────────────────────────────────────────

  generatePatch: async (integrationId: string, body: GeneratePatchRequest): Promise<ApiResponse<NLTPatchRun>> => {
    const response = await api.post<ApiResponse<NLTPatchRun>>('/nlt/patch', body, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  /**
   * POST /nlt/workflow-patch — the workflow twin of `generatePatch`. Same scope
   * (user + integration_id); the backend proposes and never writes, so applying the
   * result goes through `connector.saveWorkflow`.
   *
   * The published contract shows the response body unwrapped while every other `/nlt/*`
   * route answers in the `{success, data}` envelope, and this path has not been called
   * live yet — so accept either rather than breaking on whichever it turns out to be.
   */
  generateWorkflowPatch: async (
    integrationId: string,
    body: NltWorkflowPatchRequest
  ): Promise<NltWorkflowPatchResponse> => {
    const response = await api.post<NltWorkflowPatchResponse | ApiResponse<NltWorkflowPatchResponse>>(
      '/nlt/workflow-patch',
      body,
      { params: { integration_id: integrationId } }
    );
    const payload = response.data as ApiResponse<NltWorkflowPatchResponse>;
    if (payload && typeof payload === 'object' && 'success' in payload) {
      if (!payload.success || !payload.data) {
        throw new Error(payload.error?.message || 'Failed to generate a workflow patch');
      }
      return payload.data;
    }
    return response.data as NltWorkflowPatchResponse;
  },

  chats: async (
    integrationId: string,
    body: NLTChatRequest,
    options?: { chatType?: NLTChatType }
  ): Promise<ApiResponse<NLTChatResponse>> => {
    const response = await api.post<ApiResponse<NLTChatResponse>>('/nlt/chats', body, {
      params: {
        integration_id: integrationId,
        ...(options?.chatType ? { chat_type: options.chatType } : {}),
      },
    });
    return response.data;
  },

  // ── User: Accept / Reject Patch ───────────────────────────────────────────

  decidePatch: async (
    patchId: string,
    integrationId: string,
    body: PatchDecisionRequest
  ): Promise<ApiResponse<PatchDecisionAcceptResponse | PatchDecisionRejectResponse>> => {
    const response = await api.post<ApiResponse<PatchDecisionAcceptResponse | PatchDecisionRejectResponse>>(
      `/nlt/patches/${patchId}/decision`,
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  // ── User: History ─────────────────────────────────────────────────────────

  getHistory: async (
    integrationId: string,
    params?: { config_id?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchRun>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchRun>>>('/nlt/history', {
      params: { integration_id: integrationId, ...params },
    });
    return response.data;
  },
};
